import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row } from 'react-bootstrap';
import { messages } from '../views/Job/Job.messages';
import useFormatter from "../hooks/useFormatter";
import { paymentService } from "../services/payment";
import useJob from "../hooks/useJob";
import useSettings from "../hooks/useSettings";
import { netaxeptPreselectBanks, worldlinePreselectBanks } from "../views/Job/paymentProviders";
import { jobFetchJob, jobGetJob } from "../services/job";
import { injectIntl } from "react-intl";
import Moment from "moment/moment";

import PayplanModal from "../views/Job/components/Modals/PayplanModal";
import NewPaydateModal from "../views/Job/components/Modals/NewPaydateModal";
import MakePaymentModal from './MakePaymentModal';
import CreditorInvoicesButton from './CreditorInvoicesButton';
import InvoicePaymentActions from './InvoicePaymentActions';
import PaydateText from './PaydateText';
import PayplanDetails from './PayplanDetails';
import InvoiceStatusInfo from './InvoiceStatusInfo';
import PaymentDetails from './PaymentDetails';
import FireSafetyModal from "../views/Job/components/Modals/FireSafetyModal";
import { Vipps } from "@bambora/wallet";

const InvoiceSummaryCard = (props) => {
    const { formatCurrency } = useFormatter();
    const [payPlanConfig, setPayPlanConfig] = useState({
        paymentcount: 2,
        paymentinterval: '4w',
    });
    const [alertDialogProperties, setAlertDialogProperties] = useState({
        show: false,
        message: '',
        type: '',
    });
    const [showPayDateModal, setShowPayDateModal] = useState(false);
    const [showPayPlanModal, setShowPayPlanModal] = useState(false);
    const [showFireSafetyModal, setShowFireSafetyModal] = useState(false);
    const [serviceProvider, setServiceProvider] = useState(paymentService.paymentService);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const {
        fetchJobPaymentUrl,
        submitPayPlan,
        submitPayDate,
        shouldConfirmFireSafety,
        isCreditInvoice,
        isPaidILInvoice
    } = useJob(props.job);
    const lng = props.intl.formatMessage;
    const netaxeptBankIds = netaxeptPreselectBanks.providers.map(obj => obj.bank_id);
    const worldlineBankIds = worldlinePreselectBanks.providers.map(obj => obj.bank_id);
    const {
        preselectPayment,
        redirectToTinkForPayment,
        includedNetsBanks,
        includedWorldlineBanks
    } = useSettings();

    const canShowAllCreditorJobs = props.isAuthed ||
        (Array.isArray(props.job?.permissions) &&
        props.job?.permissions[0]?.can_show_all_creditor_jobs);

    const togglePayDateModal = () => {
        setShowPayDateModal(!showPayDateModal);
    }

    const togglePayPlanModal = () => {
        setShowPayPlanModal(!showPayPlanModal);
    }

    const toggleMakePaymentModal = () => {
        setShowPaymentModal(!showPaymentModal);
    }

    const toggleFireSafetyModal = () => {
        setShowFireSafetyModal(!showFireSafetyModal);
    }

    /**
     * Check if fire safety modal should be shown before the user can pay.
     * Fire safety information should be asked for jobs with statuscode 156 and energtytype=electricity or null
     */
    const handlePaying = () => {
        // check if fire safety information should be asked
        shouldConfirmFireSafety ? toggleFireSafetyModal() : makePaymentOrShowModal();
    }

    /**
     * Check if payment modal should be shown in the environment.
     * Payment modal contains the bank options, and for jobs with payment plan (jobstatus=41)
     * also the option of how much to pay (full amount / next installment).
     * 
     * Redirect to payment service provider's bank selection, if modal should not be shown.
     */
    const makePaymentOrShowModal = () => {
        const service = props.paymentMethod;

        // reset payment services back to environment defaults
        paymentService.resetPaymentService();

        const supportedServices = Object.values(paymentService.PAYMENTMETHODS);

        // ensure that forced service is saved
        if (supportedServices.includes(service)) {
            window.sessionStorage.setItem('paymentService', service);
        }

        // set the payment service provider
        setServiceProvider(paymentService.getPaymentService());

        if (props.job?.statuscode === 41 || preselectPayment) {
            // show the bank selection modal
            toggleMakePaymentModal();
        } else {
            // set a 'forced' payment method so payment callbacks are successful when randomizing service providers
            window.sessionStorage.setItem('paymentService', paymentService.paymentService);
            // redirect straight to payment provider page
            makePayment();
        }
    }
    
    /**
     * Close the bank selection modal and move on to make the payment
     * @param bankId
     * @param installmentToPay
     * @param phone
     */
    const handleChoosePaymentMethod = (bankId, installmentToPay, phone) => {
        // set a 'forced' payment method so payment callbacks are successful when randomizing service providers
        window.sessionStorage.setItem('paymentService', paymentService.paymentService);

        // force Netaxept as service provider if a selected bank is from Netaxept
        if (bankId !== undefined && bankId !== '' && netaxeptBankIds.includes(bankId)) {
            paymentService.setPaymentService(paymentService.PAYMENTMETHODS.NETAXEPT);
            window.sessionStorage.setItem('paymentService', paymentService.PAYMENTMETHODS.NETAXEPT);
        }

        // force Worldline as service provider if a selected bank is from Worldline
        if (bankId !== undefined && bankId !== '' && worldlineBankIds.includes(bankId)) {
            paymentService.setPaymentService(paymentService.PAYMENTMETHODS.WORLDLINE);
            window.sessionStorage.setItem('paymentService', paymentService.PAYMENTMETHODS.WORLDLINE);
        }

        toggleMakePaymentModal();
        makePayment(bankId, installmentToPay, phone);
    }

    /**
     * Creates a payment and redirects to payment service.
     * Can receive a bank id, the installment to pay and phone as parameters.
     * @param bankId
     * @param installmentToPay
     * @param phone
     */
    const makePayment = (bankId, installmentToPay, phone) => {
        props.toggleLoading(true, lng(messages.startingOnlinePayment) + "...");

        fetchJobPaymentUrl(bankId, installmentToPay, phone).then(url => {
            if (url !== null && paymentService.getPaymentService() === paymentService.PAYMENTMETHODS.WORLDLINE && bankId === 'Vipps') {
                handleWorldlineVippsPayment(url);
            } else if (url !== null) {
                window.location.href = url;
            }
        }).catch(error => {
            paymentService.resetPaymentService();
            let dialogMessage = lng(messages.pleaseTryAgain);

            // job cannot be paid in Ropo Online if agency currency and job currency don't match
            if (error.response?.data?.Message === "Agency currency and job currency do not match") {
                dialogMessage = lng(messages.cannotBePaidOnline);
            }

            props.handleAlertDialog(lng(messages.titlePaymentTerminalFailed) + ' ' + dialogMessage, 'danger');
        }).finally(() => {
            props.toggleLoading(false);
        });
    }

    /**
     * Create and start a Vipps Wallet session with Worldline.
     * https://developer.bambora.com/europe/sdk/wallet-sdk/vipps
     * 
     * @param {*} url 
     */
    const handleWorldlineVippsPayment = (url) => {
        // redirect target (optional)
        const vippsData = { target: "_top" };

        // configuration for the Vipps Wallet, url already fetched from the backend
        const vippsConfiguration = {
            sessionProvider: {
                fetchSession: () => Promise.resolve({
                    data: {
                        url: url
                    }
                })
            }
        }

        // create and start a Vipps Wallet session
        const vippsWallet = Vipps.create(vippsConfiguration, vippsData);

        vippsWallet.start().catch((error) => {
            console.error("Vipps payment failed:", error);
            // show error in alert dialog
            props.handleAlertDialog(lng(messages.errorOccurred) + ". " + lng(messages.pleaseTryAgain), 'danger');

            // reset payment service when wallet payments fail
            paymentService.resetPaymentService();
        });
    }

    /**
     * Updates state when there are changes in the inputs
     * @param event
     */
    const updatePayPlanConfig = (event) => {
        // Checks if a value is given -> not empty, or a class is removed
        if (event.target.value) {
            event.target.classList.add('not-empty');
        }
        else {
            event.target.classList.remove('not-empty');
        }

        // modify existing or create new variables
        setPayPlanConfig({ ...payPlanConfig, [event.target.id]: event.target.value });
    }

    /**
     * Sets the first pay date for a payment plan
     * @param date
     */
    const handleFirstPayDateChange = (date) => {
        const job = jobGetJob();
        job.firstpaydate = Moment(date).format('YYYY-MM-DD');
        // Updates the first paydate to state
        props.onUpdateJob(job);
    }

    /**
     * Saves a payment plan
     * @param {object} e
     */
    const handleSubmitPayPlan = async (e) => {
        e.preventDefault();

        // Shows the loading animation "Saving..."
        props.toggleLoading(true, lng(messages.actionSaving));

        try {
            const response = await submitPayPlan(payPlanConfig.paymentcount, payPlanConfig.paymentinterval);
            // checks whether or not the response indicates that paydate had been moved to workday
            const payDateMovedToWorkday = (response.data?.message === "Duedate moved to workday");

            // A successful request, gets the job's data again
            jobFetchJob(props.job.dpid).then((response) => {
                togglePayPlanModal();
                // Updates state
                props.onUpdateJob(response);
                // if notification about paydate change was received in the response, show dialog with information about it to user, else show OK dialog
                if (payDateMovedToWorkday) {
                    let message = lng(messages.payDateMovedInfoMessage) + '\n' + lng(messages.payplanSent);
                    // dialog to inform the user that the paydate has been moved to the next available workday
                    props.handleAlertDialog(message, 'success');
                } else {
                    // OK dialog
                    props.handleAlertDialog(lng(messages.payplanSent), 'success');
                }
            });
        } catch (error) {
            let message = messages.pleaseTryAgain;
            if (typeof error.response !== "undefined") {
                if (error.response.status === 403) {
                    message = messages.creatingPayplanNotAllowed;
                } else if (error.response.data.Message === "Payplan is too long") {
                    message = messages.payplanTooLong;
                }
            }
            props.toggleLoading(false);
            setAlertDialogProperties({
                message: lng(message),
                type: 'danger',
                show: true
            });
        }
    }

    /**
     * Sets a new paydate
     * @param date
     */
    const handleNewPayDateChange = (date) => {
        // replaces the job.newpaydate value set in useEffect with the datepicker value
        const job = jobGetJob();
        job.newpaydate = Moment(date).format('YYYY-MM-DD');
        props.onUpdateJob(job);
    }

    /**
     * Submits the changes in the pay date
     */
    const handleSubmitNewPayDate = async (e) => {
        e.preventDefault();

        props.toggleLoading(true, lng(messages.actionSaving));

        try {
            const response = await submitPayDate(props.job.newpaydate);
            // checks whether or not the response indicates that paydate had been moved to workday
            const payDateMovedToWorkday = (response.data?.message === "Duedate moved to workday");

            // A successful request, gets the job's data again
            jobFetchJob(props.job.dpid).then(response => {
                // hides the modal
                togglePayDateModal();
                // updates the state
                props.onUpdateJob(response);

                // if notification about paydate change was received in the patch response, show dialog with information about it to user, else show OK dialog
                if (payDateMovedToWorkday) {
                    // dialog to inform the user that the paydate has been moved to the next available workday
                    props.handleAlertDialog(lng(messages.payDateMovedInfoMessage), 'success');
                } else {
                    // OK dialog
                    props.handleAlertDialog(lng(messages.changesSaved), 'success');
                }
            });
        } catch (error) {
            props.toggleLoading(false);
            // Error is shown in the alertDialog
            setAlertDialogProperties({
                message: lng(error.response?.status === 403 ? messages.changingPaydateNotAllowed : messages.pleaseTryAgain),
                type: 'danger',
                show: true
            })
        }
    }

    const showDueDate = props.job?.payplan === null && !isCreditInvoice;

    // summary of the invoice
    return (
        <Card className={'fullwidth-mobile no-rounding-top'}>
            <Card.Body>
                <Row>
                    <Col>
                        <ul className='list-unstyled'>
                            <PaymentDetails
                                job={props.job}
                                openamount={props.job.openamount}
                                {...props}
                            />
                            {showDueDate && (
                                <li className='invoice-summary-duedate'>
                                    <span className='label'>{lng(messages.paydate)}</span>
                                    <span className='float-end'>
                                        <PaydateText
                                            intl={props.intl}
                                            statuscode={props.job.statuscode ?? null}
                                            paydate={props.job.paydate ?? null}
                                        />
                                    </span>
                                </li>
                            )}
                            {props.job.payplan !== null && (
                                <li id='invoice-summary-payplan' className='accordion-listitem'>
                                    <PayplanDetails {...props} />
                                </li>
                            )}
                            {/* Show invoice status and additional status description(s) for all jobs except IL-invoices*/}
                            {props.job?.statuscode !== 300 && (
                                <li id='invoice-summary-status'>
                                    <InvoiceStatusInfo {...props} />
                                </li>
                            )}
                        </ul>
                    </Col>
                </Row>

                <Row className='align-items-center'>
                    {canShowAllCreditorJobs && (
                        <Col xs={{ span: 12, order: 4 }} lg={{ order: 1 }} className='mt-2  mb-lg-3'>
                            <CreditorInvoicesButton job={props.job} />
                        </Col>
                    )}
                    <InvoicePaymentActions
                        job={props.job}
                        currency={props.currency}
                        buttonColor={props.buttonColor}
                        buttonTextColor={props.buttonTextColor}
                        onMovePaydate={togglePayDateModal}
                        onCreatePayplan={togglePayPlanModal}
                        onPay={handlePaying}
                    />
                    {isPaidILInvoice && (
                        <Col><span className='float-end'>{lng(messages.ILInvoicePaid)}</span></Col>
                    )}
                </Row>
                {shouldConfirmFireSafety && (
                    <FireSafetyModal
                        show={showFireSafetyModal}
                        onClose={toggleFireSafetyModal}
                        job={props.job}
                        onConfirmed={makePaymentOrShowModal}
                        toggleLoading={props.toggleLoading}
                    />
                )}
                {(props.job.statuscode === 41 || preselectPayment) && props.job?.makepayment && (
                    <MakePaymentModal
                        show={showPaymentModal}
                        serviceProvider={serviceProvider}
                        handleMakePaymentModal={toggleMakePaymentModal}
                        makePayment={makePayment}
                        handleChoosePaymentMethod={handleChoosePaymentMethod}
                        handleAlertDialog={props.handleAlertDialog}
                        includedNetsBanks={includedNetsBanks}
                        includedWorldlineBanks={includedWorldlineBanks}
                        redirectToTinkForPayment={redirectToTinkForPayment}
                        mainstatus={props.job.job_main_statuscode}
                        jobstatus={props.job.statuscode}
                        job={props?.job}
                        preselectPayment={preselectPayment}
                    />
                )}
                {props.job.dopayplan !== null && props.job.dopayplan && (
                    <PayplanModal
                        show={showPayPlanModal}
                        hide={togglePayPlanModal}
                        updateState={updatePayPlanConfig}
                        paymentcount={payPlanConfig.paymentcount}
                        payplanmaxlength={props.job.payplanmaxlength}
                        handleDatepickerChange={handleFirstPayDateChange}
                        paydate={props.job.paydate}
                        payplanfee={props.job.payplanfee > 0
                            ? formatCurrency(props.job.payplanfee, props.currency)
                            : ''}
                        handleSubmit={handleSubmitPayPlan}
                        currency={props.currency}
                        showAlertDialog={alertDialogProperties}
                        {...props}
                    />
                )}
                {props.job.modpaydate && (
                    <NewPaydateModal
                        handleDatepickerChange={handleNewPayDateChange}
                        handleSubmit={handleSubmitNewPayDate}
                        modpaydatemax={props.job.modpaydatemax}
                        modpaydatemaxdays={props.job.modpaydatemaxdays}
                        modpaydatefee={props.job.modpaydatefee > 0 ? formatCurrency(props.job.modpaydatefee, props.currency) : ''}
                        modpaydatefeeblocked={props.job.modpaydatefeeblocked}
                        modpaydatefeetext={props.job.modpaydatefeetext ?? null}
                        paydate={props.job.paydate}
                        statuscode={props.job.statuscode}
                        show={showPayDateModal}
                        handlePaydateModal={togglePayDateModal}
                        showAlertDialog={alertDialogProperties}
                        jobstatus={props.job.statuscode}
                        {...props}
                    />
                )}
            </Card.Body >
        </Card >
    );
};

InvoiceSummaryCard.propTypes = {
    'intl': PropTypes.object.isRequired,
    'job': PropTypes.object,
    'currency': PropTypes.string,
    'buttonColor': PropTypes.string,
    'buttonTextColor': PropTypes.string,
    'isAuthed': PropTypes.bool,
    'toggleLoading': PropTypes.func,
    'handleAlertDialog': PropTypes.func,
    'paymentMethod': PropTypes.string,
    'onUpdateJob': PropTypes.func
};

export default injectIntl(InvoiceSummaryCard);
